import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Reveal } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { ArtistCard } from "../components/site/ArtistCard";
import { ArtistLineup } from "../components/site/ArtistLineup";
import { PROJECT_TYPES } from "../data/site";

export default function Artists() {
  const [params, setParams] = useSearchParams();
  const activeStyle = params.get("style") || "";
  const activeProject = params.get("project") || "";

  const { data: styles = [] } = useQuery({ queryKey: ["styles"], queryFn: async () => (await api.get("/styles")).data });
  const { data: artists = [], isLoading } = useQuery({ queryKey: ["artists"], queryFn: async () => (await api.get("/artists")).data });

  const styleMap = Object.fromEntries(styles.map((s) => [s.slug, s.name]));

  const filtered = useMemo(() => {
    return artists.filter((a) => {
      const s = !activeStyle || (a.styles || []).includes(activeStyle);
      const p = !activeProject || (a.projects || []).includes(activeProject);
      return s && p;
    });
  }, [artists, activeStyle, activeProject]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value && next.get(key) !== value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <>
      <Seo title="Find Your Artist" description="Discover the right Obsidian tattoo artist for your idea. Filter by style and project type — black & grey, fine line, realism, traditional and more." />

      <section className="pt-36 lg:pt-44 pb-16 max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 border-b border-ash">
        <Reveal><p className="eyebrow mb-6">Artist finder</p></Reveal>
        <Reveal delay={60}>
          <h1 className="display text-6xl sm:text-7xl lg:text-8xl text-white">Find your artist.</h1>
        </Reveal>
        <Reveal delay={120}>
          <p className="mt-8 max-w-xl text-[#9E9EA8] text-lg leading-relaxed">
            Know what you want but not who should do it? Filter by style and project — we'll surface the artists built for the work.
          </p>
        </Reveal>
      </section>

      {/* FILTERS — top on all sizes */}
      <section data-testid="artist-filters" className="sticky top-[71px] z-30 bg-obsidian border-b border-ash">
        <div className="max-w-[1700px] mx-auto px-5 sm:px-8 lg:px-12 py-4">
          {/* filters — dropdowns on all sizes */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:max-w-3xl">
            <label className="block">
              <span className="eyebrow block mb-2">Style</span>
              <select data-testid="style-filter-select" value={activeStyle} onChange={(e) => setFilter("style", e.target.value)}
                className="w-full bg-charcoal border border-ash text-white px-3 py-3 text-sm outline-none focus:border-violet appearance-none cursor-pointer">
                <option value="">All styles</option>
                {styles.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="eyebrow block mb-2">Project</span>
              <select data-testid="project-filter-select" value={activeProject} onChange={(e) => setFilter("project", e.target.value)}
                className="w-full bg-charcoal border border-ash text-white px-3 py-3 text-sm outline-none focus:border-violet appearance-none cursor-pointer">
                <option value="">All projects</option>
                {PROJECT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </label>
          </div>
        </div>
      </section>

      {/* LINEUP — desktop only */}
      <section className="hidden lg:block border-b border-ash max-w-[1700px] mx-auto px-5 sm:px-8 lg:px-12 py-10 lg:py-14">
        <ArtistLineup artists={filtered.length ? filtered : artists} />
      </section>

      {/* RESULTS — mobile only (desktop uses the lineup above) */}
      <section className="max-w-[1700px] mx-auto px-5 sm:px-8 pt-10 pb-28 lg:hidden">
        <div className="flex items-center justify-between mb-10">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#828290]" data-testid="artist-result-count">
            {filtered.length} {filtered.length === 1 ? "artist" : "artists"}
            {activeStyle && ` — ${styleMap[activeStyle] || activeStyle}`}
          </p>
        </div>

        {isLoading ? (
          <p className="text-[#828290] font-mono text-sm">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="border border-ash p-16 text-center">
            <h3 className="font-serif text-3xl text-white mb-3">No exact match.</h3>
            <p className="text-[#9E9EA8]">Try a broader filter, or start a custom request and we'll recommend the right hand.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {filtered.map((a, i) => <ArtistCard key={a.slug} artist={a} index={i} styleMap={styleMap} />)}
          </div>
        )}
      </section>
    </>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`font-mono text-[0.66rem] uppercase tracking-[0.14em] px-3.5 py-2 border whitespace-nowrap shrink-0 transition-colors ${
        active ? "bg-violet text-obsidian border-violet" : "border-ash text-[#C4C4CC] hover:border-white hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}
